/*eslint max-lines-per-function: ["error", 240]*/
import React, { useCallback, useState } from 'react';
import { Button, Tab } from 'react-bootstrap';

import { useSpdCore } from '../../../common/hooks/useSpdCore';
import { GetCompanyDto } from '../../../common/spdCore/autogenerated/spdApiClient/models/GetCompanyDto';
import { companyData } from '../../../common/spdCore/contexts/CompanyContext';
import { TabsHistory } from '../../../app/components/TabsHistory';
import type {
  GridGetterFunction,
  GridCountFunction,
} from '../../../common/components/Grid/GridDataTypes';

import { CompanySearch } from './CompanySearch';
import { CompanyEditor } from './CompanyEditor';

enum CompanyTabs {
  ALL = 'all',
  MONTHLY = 'monthly',
  INVOICES = 'invoices',
  REFERRAL = 'referral',
}

interface CompanyListProps {
  onRowClick?: (key: string) => Promise<void> | void | undefined;
  enableSingleSelect?: boolean;
  selectedKeys?: string[];
}

export const CompanyList: React.FC<CompanyListProps> = ({
  onRowClick,
  enableSingleSelect,
  selectedKeys,
}) => {
  const spd = useSpdCore();

  const [show, setShow] = useState(false);
  const [company, setCompany] = useState<GetCompanyDto>();
  const [refresh, setRefresh] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>(CompanyTabs.ALL);

  const handleAdd = () => {
    setShow(true);
    setCompany(undefined);
  };

  const handleClose = () => {
    setShow(false);
    setRefresh(!refresh);
  };

  const handleClick = async (id: string) => {
    const getCompany = await spd.companies.get(id);
    setCompany(getCompany);
    setShow(true);
  };

  const handleOnExport = async () => {
    await spd.pdfPrinter.printCompanies(true);
  };

  const getDataAll: GridGetterFunction = useCallback(
    (
      pageSize,
      pageNumber,
      orderBy,
      orderDirection,
      filterText,
      filterColumns,
    ) =>
      spd.companies.getAllByPortion(
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
      ),
    [spd.companies],
  );

  const getCountAll: GridCountFunction = useCallback(
    (filterText, filterColumns) =>
      spd.companies.getAllCount(filterText, filterColumns),
    [spd.companies],
  );

  const getDataMonthly: GridGetterFunction = useCallback(
    (
      pageSize,
      pageNumber,
      orderBy,
      orderDirection,
      filterText,
      filterColumns,
    ) =>
      spd.companies.getAllByPortion(
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
        true,
      ),
    [spd.companies],
  );

  const getCountMonthly: GridCountFunction = useCallback(
    (filterText, filterColumns) =>
      spd.companies.getAllCount(filterText, filterColumns, true),
    [spd.companies],
  );

  const getDataInvoices: GridGetterFunction = useCallback(
    (
      pageSize,
      pageNumber,
      orderBy,
      orderDirection,
      filterText,
      filterColumns,
    ) =>
      spd.companies.getAllByPortion(
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
        undefined,
        true,
      ),
    [spd.companies],
  );

  const getCountInvoices: GridCountFunction = useCallback(
    (filterText, filterColumns) =>
      spd.companies.getAllCount(filterText, filterColumns, undefined, true),
    [spd.companies],
  );

  const getDataReferral: GridGetterFunction = useCallback(
    (
      pageSize,
      pageNumber,
      orderBy,
      orderDirection,
      filterText,
      filterColumns,
    ) =>
      spd.companies.getAllByPortion(
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
        undefined,
        undefined,
        true,
      ),
    [spd.companies],
  );

  const getCountReferral: GridCountFunction = useCallback(
    (filterText, filterColumns) =>
      spd.companies.getAllCount(
        filterText,
        filterColumns,
        undefined,
        undefined,
        true,
      ),
    [spd.companies],
  );

  const handleRowClick = onRowClick ?? handleClick;

  return (
    <>
      <TabsHistory
        activeKey={activeTab}
        setActiveTab={setActiveTab}
        id="company-list"
        className="mb-3"
        mountOnEnter
        unmountOnExit
      >
        <Tab eventKey={CompanyTabs.ALL} title="Wszystkie" className="m-3">
          <CompanySearch
            getData={getDataAll}
            getCount={getCountAll}
            onRowClick={handleRowClick}
            refresh={refresh}
            enableSingleSelect={enableSingleSelect}
            selectedKeys={selectedKeys}
            withFilterInColumns={true}
          />
        </Tab>
        <Tab
          eventKey={CompanyTabs.MONTHLY}
          title="Rozliczane miesięcznie"
          className="m-3"
        >
          <CompanySearch
            getData={getDataMonthly}
            getCount={getCountMonthly}
            onRowClick={handleRowClick}
            refresh={refresh}
            enableSingleSelect={enableSingleSelect}
            selectedKeys={selectedKeys}
            withFilterInColumns={true}
          />
        </Tab>
        <Tab
          eventKey={CompanyTabs.INVOICES}
          title="Rozliczane wg faktur"
          className="m-3"
        >
          <CompanySearch
            getData={getDataInvoices}
            getCount={getCountInvoices}
            onRowClick={handleRowClick}
            refresh={refresh}
            enableSingleSelect={enableSingleSelect}
            selectedKeys={selectedKeys}
            withFilterInColumns={true}
          />
        </Tab>
        <Tab
          eventKey={CompanyTabs.REFERRAL}
          title="Wystawiające skierowania"
          className="m-3"
        >
          <CompanySearch
            getData={getDataReferral}
            getCount={getCountReferral}
            onRowClick={handleRowClick}
            refresh={refresh}
            enableSingleSelect={enableSingleSelect}
            selectedKeys={selectedKeys}
            withFilterInColumns={true}
          />
        </Tab>
      </TabsHistory>
      <div className="d-flex justify-content-start">
        <Button variant="outline-primary" onClick={handleAdd}>
          Nowa firma
        </Button>
        <Button
          className="ms-1"
          variant="outline-primary"
          onClick={handleOnExport}
        >
          Eksportuj rozliczane miesięcznie
        </Button>
      </div>
      <CompanyEditor
        id={company?.id}
        company={companyData(company)}
        batteries={company?.companiesBatteries?.map(cb => cb.battery)}
        show={show}
        handleClose={handleClose}
        readOnly={false}
      />
    </>
  );
};
